<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    max-width="980px">

    <v-card class="receipt-settings-dialog">
      <v-toolbar dark color="info" flat>
        <v-icon class="mr-2">picture_as_pdf</v-icon>

        <v-toolbar-title>
          {{ $t('Receipt-Pdf-Logo-And-Footer-Settings') }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          icon
          dark
          :disabled="loading || saving"
          @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="receipt-settings-body">
        <v-alert
          :value="true"
          type="info"
          outline
          class="mb-3">
          {{ $t('Receipt-Pdf-Only-Notice') }}
        </v-alert>

        <v-card flat class="max-logo-info-card mb-4">
          <v-card-text>
            <div class="max-logo-info-title">
              <v-icon color="info" small>info</v-icon>
              {{ $t('Maximum-Logo-Information') }}
            </div>

            <div class="max-logo-info-text">
              {{ $t('Supported-Logo-Information') }}
            </div>
          </v-card-text>
        </v-card>

        <div v-if="loading" class="receipt-settings-loading">
          <v-progress-circular
            indeterminate
            color="info"
            :size="44">
          </v-progress-circular>

          <div class="mt-3 grey--text text--darken-1">
            {{ $t('Receipt-Pdf-Settings-Loading') }}
          </div>
        </div>

        <div v-else>
          <v-layout row wrap>
            <v-flex xs12 md6 pa-1>
              <v-card flat class="settings-section-card fill-height-card">
                <v-card-title class="settings-section-title">
                  <div>
                    <div class="settings-title-row">
                      <v-icon color="info">image</v-icon>
                      <span>{{ $t('Upper-Logo') }}</span>
                    </div>

                    <div class="settings-section-subtitle">
                      {{ $t('Header-Logo-Position-Description') }}
                    </div>
                  </div>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text>
                  <div class="logo-preview-area">
                    <img
                      v-if="settings.HeaderLogoDataUrl"
                      :src="settings.HeaderLogoDataUrl"
                      class="header-logo-preview"
                      :alt="$t('Upper-Logo')" />

                    <div v-else class="empty-logo-state">
                      <v-icon size="52" color="blue-grey lighten-3">insert_photo</v-icon>
                      <div class="mt-2">{{ $t('No-Header-Logo-Selected') }}</div>
                    </div>
                  </div>

                  <input
                    ref="headerLogoInput"
                    type="file"
                    class="hidden-file-input"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,image/jpeg,image/png,image/gif,image/bmp"
                    @change="onHeaderLogoChange" />

                  <v-layout row wrap class="mt-3">
                    <v-flex xs12 sm6 pa-1>
                      <v-btn
                        block
                        color="info"
                        outline
                        @click="selectHeaderLogo">
                        <v-icon left>file_upload</v-icon>
                        {{ $t('Select-Logo') }}
                      </v-btn>
                    </v-flex>

                    <v-flex xs12 sm6 pa-1>
                      <v-btn
                        block
                        color="red"
                        outline
                        :disabled="!settings.HeaderLogoDataUrl"
                        @click="removeHeaderLogo">
                        <v-icon left>delete_outline</v-icon>
                        {{ $t('Remove-Logo') }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12 md6 pa-1>
              <v-card flat class="settings-section-card fill-height-card">
                <v-card-title class="settings-section-title">
                  <div class="full-width">
                    <v-layout align-center>
                      <div>
                        <div class="settings-title-row">
                          <v-icon color="info">branding_watermark</v-icon>
                          <span>{{ $t('Lower-Logo') }}</span>
                        </div>

                        <div class="settings-section-subtitle">
                          {{ $t('Footer-Logo-Optional-Description') }}
                        </div>
                      </div>

                      <v-spacer></v-spacer>

                      <v-switch
                        v-model="settings.UseFooterLogo"
                        color="info"
                        hide-details
                        :label="$t('Use-Footer-Logo')">
                      </v-switch>
                    </v-layout>
                  </div>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text>
                  <div
                    class="logo-preview-area"
                    :class="{ 'logo-preview-disabled': !settings.UseFooterLogo }">
                    <img
                      v-if="settings.FooterLogoDataUrl"
                      :src="settings.FooterLogoDataUrl"
                      class="footer-logo-preview"
                      :alt="$t('Lower-Logo')" />

                    <div v-else class="empty-logo-state">
                      <v-icon size="52" color="blue-grey lighten-3">insert_photo</v-icon>
                      <div class="mt-2">{{ $t('No-Footer-Logo-Selected') }}</div>
                    </div>
                  </div>

                  <input
                    ref="footerLogoInput"
                    type="file"
                    class="hidden-file-input"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,image/jpeg,image/png,image/gif,image/bmp"
                    @change="onFooterLogoChange" />

                  <v-layout row wrap class="mt-3">
                    <v-flex xs12 sm6 pa-1>
                      <v-btn
                        block
                        color="info"
                        outline
                        :disabled="!settings.UseFooterLogo"
                        @click="selectFooterLogo">
                        <v-icon left>file_upload</v-icon>
                        {{ $t('Select-Footer-Logo') }}
                      </v-btn>
                    </v-flex>

                    <v-flex xs12 sm6 pa-1>
                      <v-btn
                        block
                        color="red"
                        outline
                        :disabled="!settings.FooterLogoDataUrl"
                        @click="removeFooterLogo">
                        <v-icon left>delete_outline</v-icon>
                        {{ $t('Remove-Logo') }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>

          <v-card flat class="settings-section-card mt-3">
            <v-card-title class="settings-section-title">
              <div>
                <div class="settings-title-row">
                  <v-icon color="info">notes</v-icon>
                  <span>{{ $t('Receipt-Pdf-Footer-Texts') }}</span>
                </div>

                <div class="settings-section-subtitle">
                  {{ $t('Receipt-Pdf-Footer-Language-Description') }}
                </div>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 md6 pa-1>
                  <v-textarea
                    v-model="settings.TrFooterText"
                    :label="$t('Turkish-Footer-Text')"
                    prepend-inner-icon="notes"
                    auto-grow
                    rows="3"
                    clearable
                    :counter="NOTE_LIMIT"
                    :error="textTooLong(settings.TrFooterText)"
                    outline>
                  </v-textarea>
                </v-flex>

                <v-flex xs12 md6 pa-1>
                  <v-textarea
                    v-model="settings.EnFooterText"
                    :label="$t('English-Footer-Text')"
                    prepend-inner-icon="translate"
                    auto-grow
                    rows="3"
                    clearable
                    :counter="NOTE_LIMIT"
                    :error="textTooLong(settings.EnFooterText)"
                    outline>
                  </v-textarea>
                </v-flex>

                <v-flex xs12 md6 pa-1>
                  <v-textarea
                    v-model="settings.DeFooterText"
                    :label="$t('German-Footer-Text')"
                    prepend-inner-icon="translate"
                    auto-grow
                    rows="3"
                    clearable
                    :counter="NOTE_LIMIT"
                    :error="textTooLong(settings.DeFooterText)"
                    outline>
                  </v-textarea>
                </v-flex>

                <v-flex xs12 md6 pa-1>
                  <v-textarea
                    v-model="settings.FrFooterText"
                    :label="$t('French-Footer-Text')"
                    prepend-inner-icon="translate"
                    auto-grow
                    rows="3"
                    clearable
                    :counter="NOTE_LIMIT"
                    :error="textTooLong(settings.FrFooterText)"
                    outline>
                  </v-textarea>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="receipt-settings-actions">
        <v-spacer></v-spacer>

        <v-btn
          flat
          color="grey darken-1"
          :disabled="saving"
          @click="cancel">
          {{ $t('cancel_button') }}
        </v-btn>

        <v-btn
          color="info"
          :loading="saving"
          :disabled="loading || notesTooLong"
          @click="save">
          <v-icon left>save</v-icon>
          {{ $t('save_button') }}
        </v-btn>
      </v-card-actions>

      <v-snackbar
        v-model="snackbar"
        :timeout="6000"
        top
        right>
        {{ message }}

        <v-btn flat color="pink" @click="snackbar = false">
          {{ $t('Close') }}
        </v-btn>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import auth from '../../../../api/auth'

export default {
  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      snackbar: false,
      message: '',

      NOTE_LIMIT: 4000,
      MAX_LOGO_FILE_SIZE: 1 * 1024 * 1024,

      settings: this.getDefaultSettings()
    }
  },

  computed: {
    notesTooLong() {
      return this.textTooLong(this.settings.TrFooterText) ||
        this.textTooLong(this.settings.EnFooterText) ||
        this.textTooLong(this.settings.DeFooterText) ||
        this.textTooLong(this.settings.FrFooterText)
    }
  },

  methods: {
    getDefaultSettings() {
      return {
        CompanyCode: 0,
        HeaderLogoDataUrl: null,
        UseFooterLogo: false,
        FooterLogoDataUrl: null,
        TrFooterText: '',
        EnFooterText: '',
        DeFooterText: '',
        FrFooterText: ''
      }
    },

    open() {
      this.dialog = true
      this.load()
    },

    cancel() {
      if (this.saving) {
        return
      }

      this.dialog = false
      this.loading = false
      this.settings = this.getDefaultSettings()
      this.resetFileInputs()
    },

    load() {
      var credentials = JSON.parse(window.localStorage.getItem('credentials'))

      if (!credentials || !credentials.SirketKodu) {
        this.showMessage(this.$t('Company-Information-Not-Found'))
        return
      }

      this.loading = true

      var params = {
        CompanyCode: credentials.SirketKodu
      }

      this.$http.post(
        auth.API_URL + 'api/CurrentMovement/ReceiptPdfSettingsGet',
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + auth.getAuthHeader()
          }
        }
      ).then((response) => {
        this.loading = false

        var data = response.body || {}

        this.settings = {
          CompanyCode: credentials.SirketKodu,
          HeaderLogoDataUrl: data.HeaderLogoDataUrl || null,
          UseFooterLogo: data.UseFooterLogo === true,
          FooterLogoDataUrl: data.FooterLogoDataUrl || null,
          TrFooterText: data.TrFooterText || '',
          EnFooterText: data.EnFooterText || '',
          DeFooterText: data.DeFooterText || '',
          FrFooterText: data.FrFooterText || ''
        }
      }, (response) => {
        this.loading = false
        this.showMessage(this.getErrorMessage(response, this.$t('Receipt-Pdf-Settings-Could-Not-Be-Loaded')))
      })
    },

    save() {
      if (this.notesTooLong) {
        this.showMessage(this.$t('Footer-Text-Max-Length'))
        return
      }

      var credentials = JSON.parse(window.localStorage.getItem('credentials'))

      if (!credentials || !credentials.SirketKodu) {
        this.showMessage(this.$t('Company-Information-Not-Found'))
        return
      }

      this.saving = true

      var params = {
        CompanyCode: credentials.SirketKodu,
        HeaderLogoDataUrl: this.settings.HeaderLogoDataUrl,
        UseFooterLogo: this.settings.UseFooterLogo === true,
        FooterLogoDataUrl: this.settings.FooterLogoDataUrl,
        TrFooterText: this.settings.TrFooterText || '',
        EnFooterText: this.settings.EnFooterText || '',
        DeFooterText: this.settings.DeFooterText || '',
        FrFooterText: this.settings.FrFooterText || ''
      }

      this.$http.post(
        auth.API_URL + 'api/CurrentMovement/ReceiptPdfSettingsSave',
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + auth.getAuthHeader()
          }
        }
      ).then((response) => {
        this.saving = false

        if (response.body && response.body.Status === false) {
          this.showMessage(response.body.ResponseMessage || this.$t('Receipt-Pdf-Settings-Could-Not-Be-Saved'))
          return
        }

        this.dialog = false
        this.resetFileInputs()
        this.$emit('saved')
      }, (response) => {
        this.saving = false
        this.showMessage(this.getErrorMessage(response, this.$t('Receipt-Pdf-Settings-Could-Not-Be-Saved')))
      })
    },

    textTooLong(value) {
      return (value || '').length > this.NOTE_LIMIT
    },

    selectHeaderLogo() {
      if (this.$refs.headerLogoInput) {
        this.$refs.headerLogoInput.click()
      }
    },

    selectFooterLogo() {
      if (this.$refs.footerLogoInput) {
        this.$refs.footerLogoInput.click()
      }
    },

    onHeaderLogoChange(event) {
      var file = event && event.target && event.target.files
        ? event.target.files[0]
        : null

      this.readLogoFile(file, (dataUrl) => {
        this.settings.HeaderLogoDataUrl = dataUrl
      })
    },

    onFooterLogoChange(event) {
      var file = event && event.target && event.target.files
        ? event.target.files[0]
        : null

      this.readLogoFile(file, (dataUrl) => {
        this.settings.FooterLogoDataUrl = dataUrl
        this.settings.UseFooterLogo = true
      })
    },

    readLogoFile(file, callback) {
      if (!file) {
        return
      }

      var supportedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/x-ms-bmp'
      ]

      if (supportedTypes.indexOf((file.type || '').toLowerCase()) === -1) {
        this.showMessage(this.$t('Invalid-Logo-File-Type'))
        this.resetFileInputs()
        return
      }

      if (file.size > this.MAX_LOGO_FILE_SIZE) {
        this.showMessage(this.$t('Logo-File-Max-Size'))
        this.resetFileInputs()
        return
      }

      var reader = new FileReader()

      reader.onload = (event) => {
        callback(event.target.result)
      }

      reader.onerror = () => {
        this.showMessage(this.$t('Logo-File-Could-Not-Be-Read'))
      }

      reader.readAsDataURL(file)
    },

    removeHeaderLogo() {
      this.settings.HeaderLogoDataUrl = null

      if (this.$refs.headerLogoInput) {
        this.$refs.headerLogoInput.value = ''
      }
    },

    removeFooterLogo() {
      this.settings.FooterLogoDataUrl = null
      this.settings.UseFooterLogo = false

      if (this.$refs.footerLogoInput) {
        this.$refs.footerLogoInput.value = ''
      }
    },

    resetFileInputs() {
      if (this.$refs.headerLogoInput) {
        this.$refs.headerLogoInput.value = ''
      }

      if (this.$refs.footerLogoInput) {
        this.$refs.footerLogoInput.value = ''
      }
    },

    showMessage(message) {
      this.message = message
      this.snackbar = true
    },

    getErrorMessage(response, fallback) {
      if (response && response.body) {
        if (response.body.ResponseMessage) {
          return response.body.ResponseMessage
        }

        if (response.body.ExceptionMessage) {
          return response.body.ExceptionMessage
        }

        if (response.body.Message) {
          return response.body.Message
        }
      }

      return fallback
    }
  }
}
</script>

<style scoped>
.receipt-settings-dialog {
  border-radius: 14px;
  overflow: hidden;
}

.receipt-settings-body {
  background: #f6f8fb;
  padding: 20px;
}

.receipt-settings-actions {
  padding: 14px 20px;
  background: #ffffff;
}

.max-logo-info-card {
  border: 1px solid #bbdefb;
  background: #f4f9ff;
  border-radius: 12px;
}

.max-logo-info-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 700;
  color: #1565c0;
  margin-bottom: 6px;
}

.max-logo-info-text {
  color: #546e7a;
  font-size: 12px;
  line-height: 1.6;
}

.settings-section-card {
  background: #ffffff;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 3px 12px rgba(31, 45, 61, 0.06);
}

.fill-height-card {
  height: 100%;
}

.settings-section-title {
  padding: 16px 18px;
}

.settings-title-row {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #263238;
  font-size: 15px;
  font-weight: 700;
}

.settings-section-subtitle {
  margin-top: 5px;
  color: #78909c;
  font-size: 12px;
  font-weight: 400;
}

.logo-preview-area {
  height: 165px;
  width: 100%;
  border: 1px dashed #b0bec5;
  background: #f8fafc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
}

.logo-preview-disabled {
  opacity: 0.45;
}

.header-logo-preview {
  max-width: 100%;
  max-height: 125px;
  object-fit: contain;
}

.footer-logo-preview {
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
}

.empty-logo-state {
  text-align: center;
  color: #90a4ae;
  font-size: 12px;
}

.hidden-file-input {
  display: none;
}

.receipt-settings-loading {
  min-height: 330px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.full-width {
  width: 100%;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mt-2 {
  margin-top: 8px;
}

.mt-3 {
  margin-top: 12px;
}

.mr-2 {
  margin-right: 8px;
}
</style>
